/**
 * Tahmin etkinliği sayfası (/etkinlik ve /etkinlik/:slug).
 *
 * Telegram postundaki bağlantı doğrudan `/etkinlik/<slug>`'a gelir. Sayfanın
 * tek işi oyuncuya üç şeyi açıkça söylemektir: **ne zamana kadar** tahmin
 * edebilir (sunucu saatine göre geri sayım), **ne gönderdi** (kayıt anıyla
 * birlikte) ve **gönderdiği değiştirilemez** — gönder düğmesi bu yüzden bir
 * onay adımı ister.
 *
 * Kapanış, tekillik ve katılım şartlarının asıl kapısı sunucudadır
 * (services/prediction.py); buradaki kontroller yalnızca oyuncunun forma
 * kadar gelip ret yemesini önler.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Target, Clock, Lock, CheckCircle2, LogIn, Trophy, Send, ShieldCheck,
  Gift, AlertTriangle, ArrowLeft, XCircle, Loader2, CalendarClock,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import client, { resolveApiAssetUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import PromoTelegramGate from '../components/PromoTelegramGate';
import { usePromoTelegram } from '../hooks/usePromoTelegram';
import {
  PHASE_LABELS, WIN_RULE_LABELS, clampScore, clockSkew, rewardText,
  splitRemaining,
} from '../lib/prediction';
import './Prediction.css';

const formatDate = (value) => (
  value ? new Date(value).toLocaleString('tr-TR', {
    day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit',
  }) : ''
);

const formatStamp = (value) => (
  value ? new Date(value).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }) : ''
);

const pad = (value) => String(value).padStart(2, '0');

const Prediction = () => {
  const { slug } = useParams();
  return slug ? <PredictionDetail slug={slug} /> : <PredictionList />;
};

/** Yayındaki etkinlikler. Tek etkinlikte de liste çizilir: bağlantı kartıdır. */
const PredictionList = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    client.get('/predictions')
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch((requestError) => {
        if (alive) setError(requestError.response?.data?.detail || 'Etkinlikler yüklenemedi');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId]);

  return (
    <main className="prd-page">
      <header className="prd-hero">
        <span className="prd-hero-icon"><Target size={22} /></span>
        <div>
          <h1>Tahmin Etkinlikleri</h1>
          <p>Maç başlamadan tahminini gönder. Her etkinlikte tek tahmin hakkın var.</p>
        </div>
      </header>

      {loading ? (
        <div className="prd-card prd-state"><Loader2 size={22} className="animate-spin" /></div>
      ) : error ? (
        <div className="prd-card prd-state"><AlertTriangle size={22} /><p>{error}</p></div>
      ) : items.length === 0 ? (
        <div className="prd-card prd-state">
          <Target size={24} />
          <p>Şu anda yayında bir tahmin etkinliği yok.</p>
        </div>
      ) : (
        <div className="prd-list">
          {items.map((item) => (
            <Link key={item.id} to={`/etkinlik/${item.slug}`} className="prd-card prd-list-item">
              <div className="prd-list-top">
                <span className={`prd-chip is-${item.phase}`}>{PHASE_LABELS[item.phase]}</span>
                {item.me && (
                  <span className="prd-chip is-mine"><CheckCircle2 size={12} /> Tahminin var</span>
                )}
              </div>
              <h2>{item.title}</h2>
              {item.kind === 'score' && (
                <div className="prd-list-match">
                  {item.home_team} <span>vs</span> {item.away_team}
                </div>
              )}
              <div className="prd-list-meta">
                <CalendarClock size={13} />
                {item.phase === 'open'
                  ? `Son tahmin: ${formatDate(item.closes_at)}`
                  : `Maç: ${formatDate(item.match_at || item.closes_at)}`}
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
};

const PredictionDetail = ({ slug }) => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [skew, setSkew] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [form, setForm] = useState({ home: '', away: '', answer: '', social: '' });
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [authOpen, setAuthOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get(`/predictions/${slug}`);
      setDetail(data);
      setSkew(clockSkew(data.server_time));
    } catch (requestError) {
      setError(requestError.response?.status === 404
        ? 'Etkinlik bulunamadı ya da yayından kaldırıldı.'
        : (requestError.response?.data?.detail || 'Etkinlik yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Efekt üyenin kimliğine bağlanır, nesnesine değil: AuthContext profili
  // arka planda tazeleyip yeni bir nesne kurar (çekiliş sayfasındaki kural).
  useEffect(() => { load(); }, [load, userId]);

  const open = detail?.phase === 'open';
  const me = detail?.me;
  const needsTelegram = Boolean(open && !me && detail?.require_telegram);
  const telegram = usePromoTelegram(needsTelegram);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  const remaining = open ? splitRemaining(detail.closes_at, now + skew) : null;
  // Geri sayım sıfıra indiğinde sayfa kendini kapatır; sunucu zaten reddeder
  // ama oyuncu formu doldurup ret yememelidir.
  const expired = open && !remaining;

  const score = detail?.kind === 'score';
  // Skor tahmini yalnızca rakamdır; metin alanı skor etkinliğinde çizilmez.
  const answerMode = score ? 'none' : (detail?.answer_mode || 'none');
  const canSubmit = useMemo(() => {
    if (!detail) return false;
    if (score && (form.home === '' || form.away === '')) return false;
    if (answerMode === 'required' && !form.answer.trim()) return false;
    if (detail.social_platform && detail.social_required && !form.social.trim()) return false;
    return true;
  }, [detail, score, form, answerMode]);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const body = {
        answer: form.answer.trim(),
        social_handle: form.social.trim(),
      };
      if (score) {
        body.home_score = Number(form.home);
        body.away_score = Number(form.away);
      }
      await client.post(`/predictions/${slug}/entry`, body);
      setConfirming(false);
      await load();
    } catch (requestError) {
      setSubmitError(requestError.response?.data?.detail || 'Tahmin gönderilemedi');
      setConfirming(false);
      if (requestError.response?.status === 409) await load();
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !detail) {
    return (
      <main className="prd-page">
        <div className="prd-card prd-state"><Loader2 size={22} className="animate-spin" /></div>
      </main>
    );
  }
  if (error || !detail) {
    return (
      <main className="prd-page">
        <div className="prd-card prd-state">
          <AlertTriangle size={24} />
          <p>{error}</p>
          <Link to="/etkinlik" className="prd-link"><ArrowLeft size={14} /> Etkinliklere dön</Link>
        </div>
      </main>
    );
  }

  const reward = rewardText(detail);
  const phase = expired ? 'closed' : detail.phase;

  return (
    <main className="prd-page">
      <Link to="/etkinlik" className="prd-link"><ArrowLeft size={14} /> Tüm etkinlikler</Link>

      <section className="prd-card prd-head">
        {detail.image_url && (
          <img className="prd-head-art" src={resolveApiAssetUrl(detail.image_url)} alt="" />
        )}
        <div className="prd-head-body">
          <span className={`prd-chip is-${phase}`}>{PHASE_LABELS[phase]}</span>
          <h1>{detail.title}</h1>
          {score && (
            <div className="prd-match">
              <span className="prd-team">{detail.home_team}</span>
              <span className="prd-vs">
                {phase === 'settled' && detail.result_home !== null
                  ? `${detail.result_home} - ${detail.result_away}`
                  : 'VS'}
              </span>
              <span className="prd-team">{detail.away_team}</span>
            </div>
          )}
          {detail.match_at && (
            <div className="prd-meta"><CalendarClock size={14} /> Maç: {formatDate(detail.match_at)}</div>
          )}
          {detail.description && <p className="prd-desc">{detail.description}</p>}
        </div>
      </section>

      {open && remaining && (
        <section className="prd-card prd-countdown">
          <span className="prd-countdown-label"><Clock size={14} /> Tahmin için kalan süre</span>
          <div className="prd-countdown-digits">
            {remaining.days > 0 && <span><b>{remaining.days}</b>gün</span>}
            <span><b>{pad(remaining.hours)}</b>saat</span>
            <span><b>{pad(remaining.minutes)}</b>dk</span>
            <span><b>{pad(remaining.seconds)}</b>sn</span>
          </div>
          <span className="prd-countdown-hint">Son tahmin: {formatDate(detail.closes_at)}</span>
        </section>
      )}

      {(reward || detail.require_telegram || detail.require_kyc || score) && (
        <section className="prd-rules">
          {reward && <span className="prd-rule is-gold"><Gift size={14} /> {reward}</span>}
          {score && (
            <span className="prd-rule"><Trophy size={14} /> Kazanma kuralı: {WIN_RULE_LABELS[detail.win_rule]}</span>
          )}
          <span className="prd-rule"><Lock size={14} /> Gönderilen tahmin değiştirilemez</span>
          {detail.require_telegram && (
            <span className="prd-rule"><Send size={14} /> Telegram topluluğu üyeliği gerekir</span>
          )}
          {detail.require_kyc && (
            <span className="prd-rule"><ShieldCheck size={14} /> Kimlik doğrulaması gerekir</span>
          )}
        </section>
      )}

      {me ? (
        <MyEntry me={me} detail={detail} phase={phase} />
      ) : phase === 'open' ? (
        !user ? (
          <section className="prd-card prd-state">
            <LogIn size={22} />
            <p>Tahmin göndermek için giriş yapın. Tahmin hesabınıza kaydedilir.</p>
            <button type="button" className="gold-btn prd-btn" onClick={() => setAuthOpen(true)}>
              <LogIn size={15} /> Giriş Yap
            </button>
          </section>
        ) : needsTelegram && !telegram.status?.member ? (
          <PromoTelegramGate
            user={user}
            loading={telegram.loading}
            status={telegram.status}
            linking={telegram.linking}
            linkUrl={telegram.linkUrl}
            onLink={telegram.link}
            onRefresh={telegram.refresh}
            purpose="prediction"
          />
        ) : (
          <section className="prd-card prd-form">
            <h2><Target size={16} /> Tahminin</h2>
            {score && (
              <div className="prd-score">
                <ScoreInput
                  label={detail.home_team}
                  value={form.home}
                  onChange={(home) => setForm({ ...form, home })}
                />
                <span className="prd-score-sep">-</span>
                <ScoreInput
                  label={detail.away_team}
                  value={form.away}
                  onChange={(away) => setForm({ ...form, away })}
                />
              </div>
            )}
            {answerMode !== 'none' && (
              <label className="prd-field">
                <span>
                  {detail.answer_label || 'Cevabın'}
                  {answerMode === 'optional' && <em> (isteğe bağlı)</em>}
                </span>
                <textarea
                  className="pusu-input"
                  rows={3}
                  maxLength={500}
                  value={form.answer}
                  onChange={(event) => setForm({ ...form, answer: event.target.value })}
                />
              </label>
            )}
            {detail.social_platform && (
              <label className="prd-field">
                <span>
                  {detail.social_label} kullanıcı adın
                  {!detail.social_required && <em> (isteğe bağlı)</em>}
                </span>
                <input
                  className="pusu-input"
                  maxLength={64}
                  placeholder="@kullaniciadi"
                  value={form.social}
                  onChange={(event) => setForm({ ...form, social: event.target.value })}
                />
              </label>
            )}
            {submitError && <div className="prd-error"><AlertTriangle size={14} /> {submitError}</div>}
            {confirming ? (
              <div className="prd-confirm">
                <p>
                  <Lock size={14} /> Tahminin gönderildikten sonra <b>değiştirilemez</b>.
                  {score && <> Skor: <b>{detail.home_team} {form.home} - {form.away} {detail.away_team}</b></>}
                </p>
                <div className="prd-confirm-actions">
                  <button type="button" className="prd-btn-ghost" disabled={submitting} onClick={() => setConfirming(false)}>
                    Vazgeç
                  </button>
                  <button type="button" className="gold-btn prd-btn" disabled={submitting} onClick={submit}>
                    {submitting ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                    Onayla ve Gönder
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="gold-btn prd-btn prd-btn-wide"
                disabled={!canSubmit}
                onClick={() => setConfirming(true)}
              >
                <Send size={15} /> Tahmini Gönder
              </button>
            )}
          </section>
        )
      ) : (
        <section className="prd-card prd-state">
          <Lock size={22} />
          <p>
            {phase === 'settled'
              ? 'Bu etkinliğe tahmin göndermediniz.'
              : 'Tahmin süresi doldu. Sonuç maç bittikten sonra bu sayfada yayınlanır.'}
          </p>
        </section>
      )}

      {phase === 'settled' && <Winners detail={detail} />}

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab="login" />
    </main>
  );
};

const ScoreInput = ({ label, value, onChange }) => (
  <label className="prd-score-box">
    <span className="prd-score-team">{label}</span>
    <div className="prd-score-row">
      <button
        type="button"
        aria-label={`${label} golünü azalt`}
        onClick={() => onChange(String(Math.max(0, Number(value || 0) - 1)))}
      >−</button>
      <input
        inputMode="numeric"
        className="pusu-input"
        value={value}
        placeholder="0"
        onChange={(event) => onChange(clampScore(event.target.value))}
      />
      <button
        type="button"
        aria-label={`${label} golünü artır`}
        onClick={() => onChange(String(Math.min(99, value === '' ? 0 : Number(value) + 1)))}
      >+</button>
    </div>
  </label>
);

/** Kayıtlı tahmin — değişmez, kayıt anıyla birlikte. */
export const MyEntry = ({ me, detail, phase }) => {
  const settled = phase === 'settled';
  return (
    <section className={`prd-card prd-mine ${settled ? (me.is_winner ? 'is-won' : 'is-lost') : ''}`}>
      <div className="prd-mine-head">
        {settled
          ? (me.is_winner ? <Trophy size={18} /> : <XCircle size={18} />)
          : <CheckCircle2 size={18} />}
        <h2>
          {settled
            ? (me.is_winner ? 'Tebrikler, doğru bildin!' : 'Bu sefer olmadı')
            : 'Tahminin kaydedildi'}
        </h2>
      </div>
      {detail.kind === 'score' && (
        <div className="prd-mine-score">
          {detail.home_team} <b>{me.home_score} - {me.away_score}</b> {detail.away_team}
        </div>
      )}
      {me.answer && <p className="prd-mine-answer">“{me.answer}”</p>}
      <div className="prd-meta"><Lock size={13} /> Kayıt zamanı: {formatStamp(me.created_at)}</div>
      {settled && me.is_winner && me.reward_status === 'delivered' && (
        <div className="prd-meta is-gold"><Gift size={13} /> Ödülün hesabına yazıldı</div>
      )}
    </section>
  );
};

const Winners = ({ detail }) => {
  const winners = detail.winners || [];
  return (
    <section className="prd-card prd-winners">
      <h2><Trophy size={16} /> Kazananlar {detail.winner_total ? `(${detail.winner_total})` : ''}</h2>
      {detail.result_answer && (
        <p className="prd-desc">Doğru cevap: <b>{detail.result_answer}</b></p>
      )}
      {detail.result_note && <p className="prd-desc">{detail.result_note}</p>}
      {winners.length === 0 ? (
        <p className="prd-muted">Bu etkinlikte doğru bilen olmadı.</p>
      ) : (
        <ul>
          {winners.map((winner, index) => (
            <li key={`${winner.username}-${index}`} className={winner.mine ? 'is-mine' : ''}>
              <span>{winner.username}</span>
              {winner.home_score !== null && winner.home_score !== undefined && (
                <b>{winner.home_score} - {winner.away_score}</b>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default Prediction;
